import React from 'react';

import Radium from '@flipdish/radium';
import { findDOMNode } from 'react-dom';
// eslint-disable-next-line import/named
import { Link, RouteComponentProps } from 'react-router';

import { Track } from '../../analytics';
import * as Api from '../../Api';
import * as B from '../../BasketControl';
import { Sorted } from '../../Data';
import * as Routes from '../../Routes';
import { Utilities } from '../../Utilities';
import Basket from '../Basket';
import ConfigureItem from '../ConfigureItem/index.weborder';
import LoadingPanel from '../LoadingPanel';
import { MainLayout } from '../MainLayout/MainLayout';
import { DispatchProps, StateProps } from '.';

const tinycolor = require('tinycolor2');

type ItemRoute = RouteComponentProps<
  {
    vrid: number;
    type: 'collection' | 'delivery';
    concessionStoreId?: number;
    id: number;
    item: number;
    index: string;
  },
  {}
>;

interface ParentProps {
  parentComponent: JSX.Element;
}

type Props = StateProps & DispatchProps & ItemRoute & ParentProps;

class ConfigureItemHolder extends React.Component<Props, {}> {
  private ensureVisible = () => {
    const domNode = findDOMNode(this);
    if (domNode) {
      Utilities.ensureVisible(domNode);
    }
  };

  public componentDidMount() {
    this.ensureVisible();
  }

  public UNSAFE_componentWillReceiveProps(newProps: Props) {
    const { CollectionSummaries, DeliverySummaries, LoadRestaurant } = newProps;

    //For loading restaurant once RestaurantSummaries have loaded
    if (
      CollectionSummaries != this.props.CollectionSummaries ||
      DeliverySummaries != this.props.DeliverySummaries
    ) {
      LoadRestaurant(newProps);
    }
  }

  private handleAddItem = (item: Api.MenuItem): (() => any) => {
    const { AddItemToBasket, OptionsState, params } = this.props;

    return () => {
      Track('Restaurants', 'Add Item To Basket Via Options', {
        event_label: item.Name,
        virtualRestaurantId: params.vrid,
        name: item.Name,
        itemType: OptionsState.Options.length === 0 ? 'simple' : 'complex',
        menuId: this.props.MenuId,
      });

      AddItemToBasket({
        Item: item,
        Options: OptionsState.Options,
      });

      this.props.router.push(Routes.ToUrl({ params }));
      // DON'T USE THIS 'NavigateTo' SHT... if we using react-router and we do then use react-router nav stuff
      // NavigateTo(Routes.ToUrl({ params }));
    };
  };

  private handleGoBack = () => {
    this.props.router.goBack();
  };

  //Basket Operations
  private handleAddItemInBasket = (payload: B.Item) => {
    this.props.AddItemToBasket(payload);
  };

  private handleRemoveItemFromBasket = (payload: {
    menuId: number;
    index: number;
    indexName?: string;
  }) => {
    const { AddToast, translate, RemoveItemFromBasket } = this.props;

    AddToast(translate('An_item_was_removed_from_your_basket') as string);
    RemoveItemFromBasket(payload);
  };

  private handleBasketCheckout = () => {
    const {
      Restaurant,
      BasketItems,
      params,
      Customer,
      Languages,
      translate,
    } = this.props;

    switch (Restaurant.status) {
      case 'pending':
      case 'none':
        return;
      default:
        break;
    }

    let initialLang = 'en';

    for (const Language of Languages) {
      if (Language.active) {
        initialLang = Language.code;
        break;
      }
    }

    // Don't start the checkout process unless the user has enough
    // value in the basket.
    const currentBasketTotal = B.Total(BasketItems.items);
    if (currentBasketTotal < Restaurant.data.MinimumOrder) {
      alert(
        translate('Minimum_order', {
          restaurantName: Restaurant.data.RestaurantName,
          amount: B.ToCurrency(
            Restaurant.data.MinimumOrder,
            Restaurant.data.Currency,
            initialLang,
          ),
        }),
      );
      return;
    }

    //Stores if alcohol is in basket
    let alcoholInBasket = false;

    //Check all items in basket for alcohol
    for (const item of BasketItems.items) {
      if (item.Item.Alcohol) {
        alcoholInBasket = true;
        break;
      }
    }

    const next = alcoholInBasket ? 'over18' : 'checkout';
    let nextUrl = `/${next}/${params.vrid}/${params.type}/${params.id}${
      params.concessionStoreId ? '/' + params.concessionStoreId : ''
    }`;

    if (Customer.status === 'success') {
      if (
        !Customer.data.RequireAgeConfirmationForAlcoholOrdersData
          .DisplayConfirmationPrompt
      ) {
        nextUrl = `/checkout/${params.vrid}/${params.type}/${params.id}${
          params.concessionStoreId ? '/' + params.concessionStoreId : ''
        }`;
      }

      Utilities.navigateTo(nextUrl);
    } else {
      // If we are not logged in then show the login box.
      this.loginAndNavigateTo();
    }
  };

  private loginAndNavigateTo = () => {
    const { PushLoginUrl, params } = this.props;

    PushLoginUrl(`/checkout/${params.vrid}/${params.type}/${params.id}`);

    if (params.type === 'delivery') {
      Utilities.navigateTo('/phone/' + params.id);
    } else {
      Utilities.navigateTo('/phone');
    }
  };

  public render() {
    const {
      Restaurant,
      Item,
      AppDesignScheme,
      isDark,
      params,
      translate,
      parentComponent,
      Offset,
      BasketItems,
      OptionsState,
      Languages,
      SetLiveOptions,
      SetOptionsState,
    } = this.props;

    const { index } = params;

    const styles = {
      nav: {
        ':hover': {},
        ':active': {},
      },
      secondaryActive: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString(),
      },
      secondaryHover: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString(),
      },
      primary: {
        color: AppDesignScheme.Primary,
      },
      secondary: {
        color: AppDesignScheme.Secondary,
      },
      tertiary: {
        color: AppDesignScheme.Tertiary,
      },
      header: {
        background: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString(),
        boxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        WebkitBoxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        borderColor: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString(),
      },
      darkHeader: {
        background: tinycolor(AppDesignScheme.Tertiary)
          .lighten(10)
          .toString(),
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString(),
        boxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
        WebkitBoxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
      },
      price: {
        borderColor: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString(),
      },
      totalHeader: {
        background: AppDesignScheme.Secondary,
      },
      total: {
        background: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString(),
        WebkitBoxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        boxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        borderColor: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString(),
      },
      darkTotal: {
        background: tinycolor(AppDesignScheme.Tertiary)
          .lighten(10)
          .toString(),
        WebkitBoxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
        boxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString(),
      },
      buttonSecondary: {
        backgroundColor: AppDesignScheme.Secondary,
        ':hover': {},
        ':active': {},
        ':focus': {},
      },
      buttonActive: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString(),
      },
      buttonHover: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString(),
      },
      hr: {
        borderColor: AppDesignScheme.Secondary,
      },
    };

    const outsideDeliveryZone: boolean = Routes.OutsideDeliveryZone(Restaurant);

    if (Item.status != 'success') {
      return <LoadingPanel AppDesignScheme={AppDesignScheme} isDark={isDark} />;
    }

    const optionSets = Sorted(Item.data.MenuItemOptionSets);

    // This code is relying on the fact that we get null
    // if we index off the end, rather than doing an index check.

    const allDone = parseInt(index) === optionSets.length;

    let bottomBar: JSX.Element | null = null;

    if (allDone) {
      bottomBar = (
        <button
          className={`fd-button fd-button--bottom fd-button--secondary visible-xs-block visible-sm-block fd-button--bottom-ios`}
          onClick={this.handleAddItem(Item.data)}
          disabled={false}
          key="done-button-bottom"
          style={
            [
              styles.buttonSecondary,
              Radium.getState(this.state, 'done-button-bottom', ':active')
                ? styles.buttonActive
                : Radium.getState(this.state, 'done-button-bottom', ':hover')
                ? styles.buttonHover
                : null,
            ] as any
          }
        >
          <h4 className="clear-margins primary" style={styles.primary}>
            {translate('Add_to_basket')}
          </h4>
        </button>
      );
    }

    const backLink =
      parseInt(index) === 0 ? (
        <Link to={Routes.ToUrl(this.props)}>
          <div
            key="fd-nav"
            className="fd-nav flex flex-align-center"
            style={styles.nav as any}
          >
            <i
              className="arrow_carrot-left tertiary"
              aria-hidden="true"
              style={
                Radium.getState(this.state, 'fd-nav', ':active')
                  ? styles.secondaryActive
                  : Radium.getState(this.state, 'fd-nav', ':hover')
                  ? styles.secondaryHover
                  : isDark
                  ? styles.primary
                  : styles.tertiary
              }
            />
            <p
              className="inline-block clear-margins tertiary bold"
              style={
                Radium.getState(this.state, 'fd-nav', ':active')
                  ? styles.secondaryActive
                  : Radium.getState(this.state, 'fd-nav', ':hover')
                  ? styles.secondaryHover
                  : isDark
                  ? styles.primary
                  : styles.tertiary
              }
            >
              <u>{translate('Back')}</u>
            </p>
          </div>
        </Link>
      ) : (
        <span className="fd-nav" onClick={this.handleGoBack}>
          <div
            key="fd-nav"
            className="fd-nav flex flex-align-center"
            style={styles.nav as any}
          >
            <i
              className="arrow_carrot-left tertiary"
              aria-hidden="true"
              style={
                Radium.getState(this.state, 'fd-nav', ':active')
                  ? styles.secondaryActive
                  : Radium.getState(this.state, 'fd-nav', ':hover')
                  ? styles.secondaryHover
                  : isDark
                  ? styles.primary
                  : styles.tertiary
              }
            />
            <h4
              className="inline-block clear-margins tertiary bold"
              style={
                Radium.getState(this.state, 'fd-nav', ':active')
                  ? styles.secondaryActive
                  : Radium.getState(this.state, 'fd-nav', ':hover')
                  ? styles.secondaryHover
                  : isDark
                  ? styles.primary
                  : styles.tertiary
              }
            >
              <u>{translate('Back')}</u>
            </h4>
          </div>
        </span>
      );

    const sidePanel = (
      <Basket
        AddItem={this.handleAddItemInBasket}
        AppDesignScheme={AppDesignScheme}
        InsideDeliveryZone={!outsideDeliveryZone}
        isDark={isDark}
        Items={BasketItems}
        MenuId={undefined}
        Offset={Offset}
        OnClick={this.handleBasketCheckout}
        parentComponent={parentComponent}
        RemoveItemAt={this.handleRemoveItemFromBasket}
        Restaurant={Restaurant}
        Screen="configure"
        translate={translate}
        vrid={params.vrid}
      />
    );

    return (
      <MainLayout>
        <div className="container-fluid flex flex-column">
          <div className="container flex-one xs-full-width">
            <div className="row configure">
              <div className="col-md-8 col-lg-8 col-sm-12">
                <h1
                  className="secondary"
                  style={isDark ? styles.primary : styles.secondary}
                >
                  {Item.data.Name}
                </h1>
                {backLink}
                <ConfigureItem
                  params={params}
                  Item={Item}
                  Restaurant={Restaurant}
                  OptionsState={OptionsState}
                  translate={translate}
                  Languages={Languages}
                  AppDesignScheme={AppDesignScheme}
                  isDark={isDark}
                  SetLiveOptions={SetLiveOptions}
                  handleAddItem={this.handleAddItem}
                  SetOptionsState={SetOptionsState}
                />
              </div>
              <div className="col-md-4 col-lg-4 col-sm-12">{sidePanel}</div>
            </div>
          </div>
          <div className="row">{bottomBar}</div>
        </div>
      </MainLayout>
    );
  }
}

export default Radium(ConfigureItemHolder);
